import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const DEFAULT_WIDTH = 30;
const MIN_WIDTH = 20;
const MAX_WIDTH = 40;

export function useResizableWidth() {
  const [width, setWidth] = useLocalStorage('chat-list-width', DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      const deltaX = e.clientX - startXRef.current;

      const deltaPercent = (deltaX / window.innerWidth) * 100;
      let newWidth = startWidthRef.current + deltaPercent;

      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

      setWidth(newWidth);
    },
    [setWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const validWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));

  return {
    width: validWidth,
    isDragging,
    handleMouseDown,
  };
}
